import type { PulseCacheKey, PulseCacheKeyPrefix } from './cache-keys';

type CacheEntry<T> = {
  value: T;
  staleAt: number;
  expiresAt: number;
};

type InFlightRequest = {
  promise: Promise<unknown>;
  generation: number;
  token: symbol;
};

type EvictionTimer = {
  timeout: ReturnType<typeof setTimeout>;
  token: symbol;
};

export type CachedValue<T> = {
  value: T;
  isStale: boolean;
};

const entries = new Map<PulseCacheKey, CacheEntry<unknown>>();
const inFlightRequests = new Map<PulseCacheKey, InFlightRequest>();
const evictionTimers = new Map<PulseCacheKey, EvictionTimer>();
const listeners = new Map<PulseCacheKey, Set<() => void>>();
const generations = new Map<PulseCacheKey, number>();

const notify = (cacheKey: PulseCacheKey) => {
  for (const listener of listeners.get(cacheKey) ?? []) {
    listener();
  }
};

const clearEvictionTimer = (cacheKey: PulseCacheKey) => {
  const evictionTimer = evictionTimers.get(cacheKey);
  if (evictionTimer) {
    clearTimeout(evictionTimer.timeout);
    evictionTimers.delete(cacheKey);
  }
};

const scheduleEviction = (cacheKey: PulseCacheKey, cacheTimeMs: number) => {
  clearEvictionTimer(cacheKey);
  const token = Symbol(cacheKey);
  const timeout = setTimeout(() => {
    if (evictionTimers.get(cacheKey)?.token !== token) {
      return;
    }

    evictionTimers.delete(cacheKey);
    entries.delete(cacheKey);
    notify(cacheKey);
  }, cacheTimeMs);
  evictionTimers.set(cacheKey, { timeout, token });
};

export const readCachedValue = <T>(
  cacheKey: PulseCacheKey,
  now = Date.now(),
): CachedValue<T> | undefined => {
  const entry = entries.get(cacheKey) as CacheEntry<T> | undefined;
  if (!entry) {
    return undefined;
  }

  if (now >= entry.expiresAt) {
    entries.delete(cacheKey);
    clearEvictionTimer(cacheKey);
    return undefined;
  }

  return { value: entry.value, isStale: now >= entry.staleAt };
};

export const fetchCachedValue = async <T>(options: {
  cacheKey: PulseCacheKey;
  fetcher: () => Promise<T>;
  staleTimeMs: number;
  cacheTimeMs: number;
  force?: boolean;
}): Promise<T> => {
  const {
    cacheKey,
    fetcher,
    staleTimeMs,
    cacheTimeMs,
    force = false,
  } = options;
  const cached = readCachedValue<T>(cacheKey);
  if (!force && cached && !cached.isStale) {
    return cached.value;
  }

  const pending = inFlightRequests.get(cacheKey);
  if (pending !== undefined) {
    return pending.promise as Promise<T>;
  }

  const generation = generations.get(cacheKey) ?? 0;
  const token = Symbol(cacheKey);
  const request = (async () => {
    try {
      const value = await fetcher();
      if ((generations.get(cacheKey) ?? 0) === generation) {
        const now = Date.now();
        entries.set(cacheKey, {
          value,
          staleAt: now + staleTimeMs,
          expiresAt: now + cacheTimeMs,
        });
        scheduleEviction(cacheKey, cacheTimeMs);
        notify(cacheKey);
      }
      return value;
    } finally {
      if (inFlightRequests.get(cacheKey)?.token === token) {
        inFlightRequests.delete(cacheKey);
      }
    }
  })();

  inFlightRequests.set(cacheKey, { promise: request, generation, token });
  return request;
};

export const subscribeToCache = (
  cacheKey: PulseCacheKey,
  listener: () => void,
): (() => void) => {
  const cacheListeners = listeners.get(cacheKey) ?? new Set<() => void>();
  cacheListeners.add(listener);
  listeners.set(cacheKey, cacheListeners);

  return () => {
    cacheListeners.delete(listener);
    if (cacheListeners.size === 0) {
      listeners.delete(cacheKey);
    }
  };
};

export const invalidateCache = (cacheKeyPrefix: PulseCacheKeyPrefix) => {
  const knownKeys = new Set([
    ...entries.keys(),
    ...inFlightRequests.keys(),
    ...evictionTimers.keys(),
    ...listeners.keys(),
  ]);
  const matchingKeys = [...knownKeys].filter(cacheKey =>
    cacheKey.startsWith(cacheKeyPrefix),
  );
  for (const cacheKey of matchingKeys) {
    generations.set(cacheKey, (generations.get(cacheKey) ?? 0) + 1);
    entries.delete(cacheKey);
    inFlightRequests.delete(cacheKey);
    clearEvictionTimer(cacheKey);
    notify(cacheKey);
  }
};

export const resetQueryCache = () => {
  for (const cacheKey of evictionTimers.keys()) {
    clearEvictionTimer(cacheKey);
  }
  entries.clear();
  inFlightRequests.clear();
  listeners.clear();
  generations.clear();
};
